import { useState, useEffect } from "react";
import { api } from "../api.js";
import { card, labelStyle, inputStyle, selectStyle, btnPrimary, btnSecondary, btnCompact, SectionTitle, StepBadge, Badge } from "./ui.js";
import BulkPropertyCreate from "./BulkPropertyCreate.js";
import WebhookSetupCard from "./WebhookSetupCard.js";
import ClientIdEditForm from "./ClientIdEditForm.js";
import SendSettingsView, { type SendSettingsTarget } from "./SendSettingsView.js";
import { summarizeOverrides, type OverrideRow } from "./clientSendOverrideModel.js";

interface ClientId {
  id: string;
  label: string;
  environment: string;
  isDefault: boolean;
  maskedClientId: string;
  /** null = デフォルト送信設定を継承 / オブジェクト = 個別設定あり */
  sendSettings: Record<string, unknown> | null;
}

export default function ConnectionTab() {
  const [clientIds, setClientIds] = useState<ClientId[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  // Client ID ごとの「誰に・何を送るか」の上書き。一覧の要約に使う
  const [overridesByClient, setOverridesByClient] = useState<Record<string, OverrideRow[]>>({});
  const [objectLabels, setObjectLabels] = useState<Record<string, string>>({});

  /** 送信設定のドリルダウン先。null なら一覧を表示 */
  const [editing, setEditing] = useState<SendSettingsTarget | null>(null);

  // 新規追加フォーム
  const [label, setLabel] = useState("");
  const [clientId, setClientId] = useState("");
  const [env, setEnv] = useState("sandbox");
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ [id: string]: { success: boolean; message: string } }>({});
  const [testing, setTesting] = useState<string | null>(null);
  /** 編集フォームを開いているクライアントID */
  const [editingRow, setEditingRow] = useState<string | null>(null);

  const load = async () => {
    try {
      const data = await api.getClientIds();
      const list: ClientId[] = data.clientIds || [];
      setClientIds(list);

      // 一覧の要約に使う。失敗しても Client ID 一覧の表示は止めない
      try {
        const schemas = await api.getObjectSchemas();
        setObjectLabels(
          Object.fromEntries([
            ...schemas.standard.map((o) => [o.key, o.label]),
            ...schemas.custom.map((o) => [o.key, `${o.label}（カスタム）`]),
          ]),
        );
        const entries = await Promise.all(
          list.map(async (c) => [c.id, (await api.getClientSendOverrides(c.id)).overrides] as const),
        );
        setOverridesByClient(Object.fromEntries(entries));
      } catch {
        setOverridesByClient({});
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // 編集画面から戻ったときにも読み直して、個別設定バッジを最新にする
  useEffect(() => { if (!editing) load(); }, [editing]);

  const handleAdd = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.createClientId({ label, clientId, environment: env, isDefault: isDefault || clientIds.length === 0 });
      setLabel("");
      setClientId("");
      setEnv("sandbox");
      setIsDefault(false);
      setShowAddForm(false);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await api.updateClientId(id, { isDefault: true });
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDelete = async (id: string) => {
    // 削除の影響範囲が見えていないと事故になる。連動して消えるものを明記する
    const ok = confirm(
      [
        "このクライアントIDを削除しますか？",
        "",
        "・このクライアントIDで過去に送信した書類のステータス同期は停止します",
        "・このクライアントID専用の送信設定も削除されます",
      ].join("\n"),
    );
    if (!ok) return;
    try {
      await api.deleteClientId(id);
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleTest = async (id: string) => {
    setTesting(id);
    try {
      const result = await api.testClientId(id);
      setTestResult((prev) => ({ ...prev, [id]: result }));
    } catch (e: any) {
      setTestResult((prev) => ({ ...prev, [id]: { success: false, message: e.message } }));
    } finally {
      setTesting(null);
    }
  };

  // 送信設定の編集画面
  if (editing) {
    return <SendSettingsView target={editing} onBack={() => setEditing(null)} />;
  }

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#6b6b68" }}>読み込み中...</div>;

  return (
    <>
      <SectionTitle>接続設定</SectionTitle>

      {error && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FCEBEB", color: "#A32D2D", fontSize: 14, marginBottom: 16 }}>{error}</div>
      )}

      {/* HubSpot 接続管理 */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>HubSpot 接続</div>
          <button
            onClick={() => { window.location.href = "/api/auth/authorize"; }}
            style={btnCompact}
          >
            再接続（アカウント変更）
          </button>
        </div>
        <div style={{ fontSize: 13, color: "#6b6b68", lineHeight: 1.7 }}>
          HubSpot ポータルとの OAuth 接続を管理します。別のポータルに切り替える場合は「再接続」をクリックしてください。
        </div>
      </div>

      {/* クライアントID一覧 */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>クライアントID一覧</div>
          <button onClick={() => setShowAddForm(!showAddForm)} style={{ ...btnPrimary, fontSize: 13 }}>
            {showAddForm ? "キャンセル" : "+ 新規追加"}
          </button>
        </div>
        <div style={{ fontSize: 13, color: "#6b6b68", marginBottom: 18, lineHeight: 1.7 }}>
          送信に使うクラウドサインのクライアントIDを登録します。送信モード・転送設定・敬称は
          「デフォルト設定」で一括指定でき、クライアントIDごとに上書きすることもできます。
        </div>

        {/* デフォルト送信設定（個別設定を持たない全 Client ID に適用される） */}
        <div style={{
          display: "flex", alignItems: "center", gap: 16,
          padding: "14px 16px", border: "1px solid #e8e7e3", borderRadius: 10,
          background: "#FAFAFE", marginBottom: 16,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>デフォルト設定</div>
            <div style={{ fontSize: 13, color: "#6b6b68", lineHeight: 1.6 }}>
              送信モード・書類の転送設定・受信者氏名の敬称と、オブジェクトごとの送信設定（宛先・ファイル・テンプレート・署名欄の位置・格納先）。
              個別設定を持たないクライアントIDに適用されます。
            </div>
          </div>
          <button
            onClick={() => setEditing({ kind: "default" })}
            style={{ ...btnCompact, borderColor: "#534AB7", color: "#534AB7" }}
          >
            編集
          </button>
        </div>

        {clientIds.length === 0 && !showAddForm && (
          <div style={{ padding: "24px 0", textAlign: "center", color: "#6b6b68", fontSize: 15 }}>
            クライアントIDが登録されていません。「+ 新規追加」から登録してください。
          </div>
        )}

        {clientIds.map((c) => {
          const result = testResult[c.id];
          // 「送信の挙動」か「誰に・何を送るか」のどちらかに個別設定があれば「個別」。
          // 新しいバッジ体系を並列に作らず、既存のバッジを拡張する
          const behaviorCustom = !!c.sendSettings && Object.keys(c.sendSettings).length > 0;
          const rows = overridesByClient[c.id] ?? [];
          const hasCustom = behaviorCustom || rows.length > 0;
          // バッジだけでは何が個別なのか分からないため、要約を1行添える
          const summaryParts: string[] = [];
          if (behaviorCustom) summaryParts.push("送信の挙動を個別設定");
          if (rows.length > 0) summaryParts.push(summarizeOverrides(rows, (k) => objectLabels[k] ?? k));
          return (
            <div key={c.id} style={{ padding: "16px 0", borderTop: "1px solid #f0efeb" }}>
              {/*
                左に名前・バッジ・ID・要約、右に操作。狭い画面では操作が下に回り込む。
                「削除」は誤操作の影響が大きいので、区切りを入れて右端に固定する
                （「デフォルトに設定」が無い行でも位置がずれないよう、その場所は空けておく）
              */}
              <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 320px", minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{c.label}</div>
                {c.isDefault && <Badge bg="#EEEDFE" color="#534AB7">デフォルト</Badge>}
                <Badge bg={c.environment === "production" ? "#E6F1FB" : "#FAEEDA"} color={c.environment === "production" ? "#185FA5" : "#854F0B"}>
                  {c.environment === "production" ? "本番" : "サンドボックス"}
                </Badge>
                {hasCustom
                  ? <Badge bg="#E1F5EE" color="#0F6E56">送信設定：個別</Badge>
                  : <Badge bg="#F0EFEB" color="#6b6b68">送信設定：デフォルト</Badge>
                }
              </div>
              <div style={{ fontSize: 13, color: "#6b6b68", fontFamily: "monospace" }}>{c.maskedClientId}</div>
              {summaryParts.length > 0 && (
                <div style={{ fontSize: 13, color: "#6b6b68", marginTop: 4 }}>
                  {summaryParts.join(" ／ ")}
                </div>
              )}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <button
                  onClick={() => setEditing({ kind: "client", id: c.id, label: c.label })}
                  style={{ ...btnCompact, borderColor: "#534AB7", color: "#534AB7" }}
                >
                  送信設定
                </button>
                <button onClick={() => handleTest(c.id)} disabled={testing === c.id} style={btnCompact}>
                  {testing === c.id ? "テスト中..." : "接続テスト"}
                </button>
                <button
                  onClick={() => setEditingRow(editingRow === c.id ? null : c.id)}
                  // 「編集を閉じる」に切り替わっても幅が変わらず、左のボタンがずれないようにする
                  style={{ ...btnCompact, minWidth: 124 }}
                >
                  {editingRow === c.id ? "編集を閉じる" : "名前・IDを編集"}
                </button>
                <button
                  onClick={() => handleSetDefault(c.id)}
                  disabled={c.isDefault}
                  aria-hidden={c.isDefault}
                  tabIndex={c.isDefault ? -1 : 0}
                  style={{ ...btnCompact, visibility: c.isDefault ? "hidden" : "visible" }}
                >
                  デフォルトに設定
                </button>
                <span aria-hidden style={{ width: 1, height: 20, background: "#e8e7e3", margin: "0 4px" }} />
                <button onClick={() => handleDelete(c.id)} style={{ ...btnCompact, color: "#A32D2D", borderColor: "#f0c8c8" }}>
                  削除
                </button>
              </div>
              </div>

              {result && (
                <div style={{
                  padding: "8px 12px", borderRadius: 8, fontSize: 13, marginTop: 12,
                  background: result.success ? "#E1F5EE" : "#FCEBEB",
                  color: result.success ? "#0F6E56" : "#A32D2D",
                }}>
                  {result.success ? "接続成功" : `接続失敗: ${result.message}`}
                </div>
              )}

              {editingRow === c.id && (
                <ClientIdEditForm
                  row={{ id: c.id, label: c.label, environment: c.environment, maskedClientId: c.maskedClientId }}
                  onSaved={() => { setEditingRow(null); load(); }}
                  onCancel={() => setEditingRow(null)}
                />
              )}
            </div>
          );
        })}

        {showAddForm && (
          <div style={{ borderTop: "1px solid #f0efeb", marginTop: 16, paddingTop: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>新規クライアントID</div>

            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>ラベル（送信者を識別するための名前）</div>
              <input style={inputStyle} type="text" placeholder="例: 営業部、法務部、高山" value={label} onChange={(e) => setLabel(e.target.value)} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>環境</div>
              <select style={selectStyle} value={env} onChange={(e) => setEnv(e.target.value)}>
                <option value="sandbox">サンドボックス（api-sandbox.cloudsign.jp）</option>
                <option value="production">本番環境（api.cloudsign.jp）</option>
              </select>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>クライアントID</div>
              <input style={inputStyle} type="text" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={clientId} onChange={(e) => setClientId(e.target.value)} />
              <div style={{ fontSize: 12, color: "#6b6b68", marginTop: 4 }}>クラウドサイン管理画面で発行した36桁のID</div>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, fontSize: 14, cursor: "pointer" }}>
              <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
              デフォルト送信者に設定する
            </label>

            <div style={{ display: "flex", gap: 10 }}>
              <button style={{ ...btnPrimary, opacity: (!label || !clientId) ? 0.5 : 1 }}
                onClick={handleAdd} disabled={saving || !label || !clientId}>
                {saving ? "追加中..." : "追加"}
              </button>
              <button style={btnSecondary} onClick={() => setShowAddForm(false)}>キャンセル</button>
            </div>
          </div>
        )}
      </div>

      <BulkPropertyCreate />

      {/* セットアップ手順 */}
      <div style={{ ...card, background: "#faf9f7" }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 14 }}>セットアップ手順</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, fontSize: 15, color: "#4a4a48" }}>
          {[
            { num: 1, title: "Web APIを有効化", desc: "クラウドサイン管理画面 → チーム設定 → Web API →「利用する」" },
            { num: 2, title: "クライアントIDを発行", desc: "送信者ごとにクラウドサイン管理画面でクライアントIDを発行" },
            { num: 3, title: "クライアントID一覧から登録", desc: "「+ 新規追加」で複数の送信者を登録できます" },
            { num: 4, title: "接続テストで疎通確認", desc: "各クライアントIDごとにテスト可能" },
            { num: 5, title: "設定を確認", desc: "「デフォルト設定」で格納先プロパティ・送信モード・宛先などを設定" },
          ].map((s) => (
            <div key={s.num} style={{ display: "flex", alignItems: "flex-start" }}>
              <StepBadge num={s.num} done={false} />
              <div>
                <span style={{ fontWeight: 600 }}>{s.title}</span><br />
                <span style={{ color: "#6b6b68", fontSize: 13 }}>{s.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <WebhookSetupCard />

      <div style={{ ...card, borderLeft: "3px solid #BA7517", borderRadius: "0 12px 12px 0" }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#854F0B", marginBottom: 8 }}>注意事項</div>
        <ul style={{ fontSize: 14, color: "#6b6b68", lineHeight: 2.2, paddingLeft: 18, margin: 0 }}>
          <li>クライアントIDは個人ごとに発行されます。書類の送信者 = ID発行者です。</li>
          <li>複数の送信者（部署・担当者）を登録できます。送信時にどのクライアントIDから送るか選択できます。</li>
          <li>各設定はクライアントIDごとに上書きできます。上書きしなかった項目はデフォルト設定に追従します。</li>
          <li>アクセストークンは1時間で期限切れ。本アプリが自動で再取得します。</li>
          <li>クラウドサインのスタンダードプラン以上が必要です。</li>
        </ul>
      </div>
    </>
  );
}
